/**
 * Combos y recetas (spec 20): la lista de los combos de cantidades fijas y las recetas por medida, con su búsqueda, su
 * filtro por estado y su paginación. Un clic abre el combo; lo crean y lo cambian quienes tienen «Descuentos
 * programados» (por plantilla, la dueña). Se venden desde el punto de venta y las cotizaciones.
 */
import { Money, UNIDAD_MEDIDA_ETIQUETA, type ComboResumenDTO } from '@mg/shared';
import { ArrowLeft, Layers, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { PieDePaginas } from '@/componentes/PieDePaginas.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Filtro } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { useAuth } from '@/lib/auth.tsx';
import { useCombos, type FiltrosCombos } from './api.ts';
import { EditorCombo } from './EditorCombo.tsx';

type Estado = NonNullable<FiltrosCombos['activo']>;
const ESTADOS: Record<Estado, string> = { true: 'Activos', false: 'Desactivados', todos: 'Todos' };
/** Combos por página. */
const LIMITE = 25;

/** «Combo» o «Receta por m²». */
const tipoCorto = (k: Pick<ComboResumenDTO, 'tipo' | 'unidad_medida'>) => (k.tipo === 'fijo' ? 'Combo' : `Receta por ${UNIDAD_MEDIDA_ETIQUETA[k.unidad_medida ?? 'm2']}`);

export function Combos() {
  const { puede, usuario } = useAuth();
  // Un combo vale en todas las tiendas: lo configura la dueña (con el permiso de descuentos, que por plantilla es suyo)
  const puedeEditar = puede('productos.descuentos') && usuario?.rol === 'superadmin';
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState<Estado>('true');
  const [pagina, setPagina] = useState(1);
  /** Ventana abierta: `id` null = combo nuevo. */
  const [editor, setEditor] = useState<{ id: string | null } | null>(null);
  const lista = useCombos({ q: q.trim() || undefined, activo: estado, pagina, limite: LIMITE });

  return (
    <>
      <TituloPagina
        titulo="Combos y recetas"
        subtitulo="Vende de una vez un trabajo de varios productos: un kit con cantidades fijas o una receta por medida, como el techo en dry wall por m². El descuento del combo solo vale si se vende completo."
        acciones={
          <>
            <Button variant="outline" asChild>
              <Link to="/productos" data-testid="combos-volver">
                <ArrowLeft /> Productos
              </Link>
            </Button>
            {puedeEditar ? (
              <Button onClick={() => setEditor({ id: null })} data-testid="btn-nuevo-combo">
                <Plus /> Nuevo combo o receta
              </Button>
            ) : null}
          </>
        }
      />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código o nombre…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPagina(1);
            }}
            className="w-80 pl-8"
            autoFocus
            aria-label="Buscar combo o receta"
            data-testid="buscar-combo"
          />
        </div>
        <Filtro etiqueta="Estado">
          <Select
            value={estado}
            onChange={(e) => {
              setEstado(e.target.value as Estado);
              setPagina(1);
            }}
            className="w-44"
            aria-label="Estado"
            data-testid="filtro-estado-combo"
          >
            {(Object.keys(ESTADOS) as Estado[]).map((x) => (
              <option key={x} value={x}>{ESTADOS[x]}</option>
            ))}
          </Select>
        </Filtro>
      </div>
      <EstadoQuery cargando={lista.isLoading} error={lista.error} datos={lista.data} reintentar={() => void lista.refetch()}>
        {(d) =>
          d.datos.length === 0 ? (
            <Vacio
              icono={<Layers className="h-6 w-6" />}
              titulo={q || estado !== 'true' ? 'Sin combos' : 'Todavía no hay combos'}
              descripcion={q ? `Nada coincide con «${q}».` : estado === 'false' ? 'No hay combos desactivados.' : 'Crea el primero: un kit de productos o una receta por m² como la del techo en dry wall.'}
              accion={puedeEditar && !q && estado !== 'false' ? <Button onClick={() => setEditor({ id: null })} data-testid="vacio-nuevo-combo">Nuevo combo o receta</Button> : undefined}
            />
          ) : (
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Productos</TableHead>
                    <TableHead className="text-right">Descuento</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.datos.map((k) => {
                    const descuento = Money.parse(k.descuento_pct);
                    return (
                      <TableRow key={k.id} className="cursor-pointer" onClick={() => setEditor({ id: k.id })} data-testid={`combo-${k.codigo}`}>
                        <TableCell className="font-mono text-xs">{k.codigo}</TableCell>
                        <TableCell className="font-medium">{k.nombre}</TableCell>
                        <TableCell>{tipoCorto(k)}</TableCell>
                        <TableCell className="text-right num">{k.componentes}</TableCell>
                        <TableCell className="text-right num">{descuento.esPositivo() ? `${descuento.toDisplay()} %` : <span className="text-muted-foreground">sin descuento</span>}</TableCell>
                        <TableCell>
                          <span className="flex flex-wrap gap-1">
                            <Badge variant={k.activo ? 'ok' : 'neutro'}>{k.activo ? 'Activo' : 'Desactivado'}</Badge>
                            {!k.vendible ? <Badge variant="critico" title="Uno de sus productos está desactivado: no se puede vender hasta corregirlo">Producto desactivado</Badge> : null}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <PieDePaginas total={d.total} limite={d.limite} pagina={pagina} setPagina={setPagina} nombre={d.total === 1 ? 'combo' : 'combos'} testid="combos-paginas" />
            </div>
          )
        }
      </EstadoQuery>
      <EditorCombo abierto={!!editor} comboId={editor?.id ?? null} cerrar={() => setEditor(null)} abrir={(id) => setEditor({ id })} />
    </>
  );
}
